import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, ArrowRight, Briefcase, ShieldCheck, LogOut, Users, User, CalendarClock, CheckSquare, PieChart, Clock, Wallet, TrendingUp, Coins } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { RoleBadge, StatusBadge } from "@/components/badges";
import { formatDate, meetingTypeLabel, nok } from "@/lib/format";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AiAssistant } from "@/components/AiAssistant";
import { SigningTasks } from "@/components/SigningTasks";
import { VerifyIdentity } from "@/components/VerifyIdentity";
import { AddCompany } from "@/components/AddCompany";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function initials(name = "") {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();
  const [companies, setCompanies] = useState(null);
  const [dash, setDash] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const loadCompanies = () => api.get("/companies").then((r) => setCompanies(r.data)).catch(() => setCompanies([]));

  useEffect(() => {
    loadCompanies();
    api.get("/dashboard").then((r) => setDash(r.data)).catch(() => setDash({}));
  }, []);

  const firstName = user?.name?.split(" ")[0] || "";

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="sticky top-0 z-40 flex h-16 items-center border-b border-border/60 bg-background/80 px-5 backdrop-blur-xl sm:px-8">
        <Logo subtitle="Styrerom" />
        <div className="ml-auto flex items-center gap-1.5">
          <Button variant="outline" size="sm" className="gap-2" data-testid="portfolio-link" onClick={() => navigate("/portefolje")}>
            <PieChart className="h-4 w-4" /> Min portefølje
          </Button>
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu" className="flex items-center gap-2 rounded-full p-1 transition-colors hover:bg-muted">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.avatar || undefined} />
                  <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">{initials(user?.name)}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs font-normal text-muted-foreground">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="menu-profile-link" onClick={() => navigate("/profil")}>
                <User className="mr-2 h-4 w-4" /> Min profil
              </DropdownMenuItem>
              {user?.is_admin ? (
                <DropdownMenuItem data-testid="menu-admin-link" onClick={() => navigate("/admin")}>
                  <ShieldCheck className="mr-2 h-4 w-4" /> Admin
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }}>
                <LogOut className="mr-2 h-4 w-4" /> Logg ut
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8 lg:py-14">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Velkommen</p>
          <h1 className="mt-2 font-headings text-3xl font-extrabold tracking-tight sm:text-4xl">
            Hei, {firstName}
          </h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Velg et selskap for å gå inn i styrerommet. Rollene dine hentes automatisk fra
            Brønnøysundregisteret og Proff.
          </p>
        </motion.div>

        <VerifyIdentity user={user} onDone={refreshUser} />

        <SigningTasks />

        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <button onClick={() => navigate("/oversikt/selskaper")} className="group rounded-xl border border-border/60 bg-card p-5 text-left transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md" data-testid="kpi-companies">
            <Briefcase className="h-5 w-5 text-primary" strokeWidth={1.7} />
            <p className="mt-3 font-headings text-2xl font-extrabold">{dash ? dash.companies_count : "–"}</p>
            <p className="text-xs text-muted-foreground">Selskaper</p>
          </button>
          <button onClick={() => navigate("/oversikt/styreverv")} className="group rounded-xl border border-border/60 bg-card p-5 text-left transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md" data-testid="kpi-board-roles">
            <Users className="h-5 w-5 text-primary" strokeWidth={1.7} />
            <p className="mt-3 font-headings text-2xl font-extrabold">{dash ? dash.board_roles : "–"}</p>
            <p className="text-xs text-muted-foreground">Styreverv</p>
          </button>
          <button onClick={() => navigate("/oversikt/moter")} className="group rounded-xl border border-border/60 bg-card p-5 text-left transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md" data-testid="kpi-upcoming-meetings">
            <CalendarClock className="h-5 w-5 text-primary" strokeWidth={1.7} />
            <p className="mt-3 font-headings text-2xl font-extrabold">{dash ? (dash.upcoming_meetings?.length || 0) : "–"}</p>
            <p className="text-xs text-muted-foreground">Kommende møter</p>
          </button>
          <button onClick={() => navigate("/oversikt/oppgaver")} className="group rounded-xl border border-border/60 bg-card p-5 text-left transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md" data-testid="kpi-open-tasks">
            <CheckSquare className="h-5 w-5 text-primary" strokeWidth={1.7} />
            <p className="mt-3 font-headings text-2xl font-extrabold">{dash ? dash.my_open_tasks_count : "–"}</p>
            <p className="text-xs text-muted-foreground">Mine åpne oppgaver</p>
          </button>
        </div>

        {dash?.financials ? (
          <div className="mt-6 grid gap-4 sm:grid-cols-3" data-testid="dash-financials">
            <div className="rounded-xl border border-border/60 bg-card p-5">
              <Coins className="h-5 w-5 text-primary" strokeWidth={1.7} />
              <p className="mt-3 font-headings text-2xl font-extrabold tabular-nums">{nok(dash.financials.total_revenue)}</p>
              <p className="text-xs text-muted-foreground">Samlet omsetning ({dash.financials.year}) · alle selskaper</p>
            </div>
            <div className="rounded-xl border border-border/60 bg-card p-5">
              <TrendingUp className="h-5 w-5 text-primary" strokeWidth={1.7} />
              <p className={`mt-3 font-headings text-2xl font-extrabold tabular-nums ${dash.financials.total_result < 0 ? "text-destructive" : ""}`}>{nok(dash.financials.total_result)}</p>
              <p className="text-xs text-muted-foreground">Samlet resultat ({dash.financials.year}) · hittil i år</p>
            </div>
            <button onClick={() => setShareOpen(true)} className="group rounded-xl border border-primary/30 bg-primary/[0.06] p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md" data-testid="dash-my-profit-share">
              <div className="flex items-start justify-between">
                <Wallet className="h-5 w-5 text-primary" strokeWidth={1.7} />
                <ArrowRight className="h-4 w-4 -translate-x-1 text-primary opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
              </div>
              <p className="mt-3 font-headings text-2xl font-extrabold tabular-nums text-primary">{nok(dash.financials.my_profit_share)}</p>
              <p className="text-xs text-muted-foreground">Din andel av overskuddet · se fordeling per selskap</p>
            </button>
          </div>
        ) : null}

        <Dialog open={shareOpen} onOpenChange={setShareOpen}>
          <DialogContent data-testid="profit-share-dialog">
            <DialogHeader>
              <DialogTitle>Din andel av overskuddet</DialogTitle>
              <DialogDescription>Fordeling per selskap ({dash?.financials?.year}), beregnet etter din eierandel.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {(dash?.financials?.my_profit_share_breakdown || []).map((b) => (
                <div key={b.company_id} data-testid={`share-row-${b.company_id}`} className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-card p-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{b.company_name}</p>
                    <p className="text-xs text-muted-foreground">Eierandel {b.ownership_pct} % · resultat {nok(b.result)}</p>
                  </div>
                  <p className={`shrink-0 font-headings text-base font-extrabold tabular-nums ${b.share < 0 ? "text-destructive" : "text-primary"}`}>{nok(b.share)}</p>
                </div>
              ))}
              {(dash?.financials?.my_profit_share_breakdown || []).length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">Fant ingen eierandel i selskapene dine ennå.</p>
              ) : null}
            </div>
            <div className="mt-2 flex items-center justify-between border-t border-border/60 pt-3">
              <span className="text-sm font-medium">Sum</span>
              <span className="font-headings text-lg font-extrabold tabular-nums text-primary">{nok(dash?.financials?.my_profit_share)}</span>
            </div>
          </DialogContent>
        </Dialog>

        <div className="mt-10">
          <AiAssistant />
        </div>

        <div className="mt-10 flex items-center gap-2">
          <h2 className="font-headings text-lg font-bold tracking-tight">Dine selskaper</h2>
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {companies ? companies.length : ""}
          </span>
          <div className="ml-auto">
            <AddCompany verified={!!user?.identity_verified} onAdded={loadCompanies} />
          </div>
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {companies === null
            ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-44 rounded-xl" />)
            : companies.map((c, i) => (
                <motion.button
                  key={c.id}
                  data-testid={`company-card-${c.id}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.05 }}
                  onClick={() => navigate(`/selskap/${c.id}`)}
                  className="group flex flex-col rounded-xl border border-border/60 bg-card p-6 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md"
                >
                  <div className="flex items-start justify-between">
                    <div className="grid h-11 w-11 place-items-center rounded-lg bg-primary/10 text-primary">
                      <Building2 className="h-5 w-5" strokeWidth={1.7} />
                    </div>
                    <ArrowRight className="h-4 w-4 -translate-x-1 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                  </div>
                  <h3 className="mt-4 font-headings text-base font-bold leading-tight tracking-tight">{c.name}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {c.org_form} · Org.nr {c.org_number} · {c.city}
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {(c.my_roles?.length ? c.my_roles : [c.my_role]).map((r) => (
                      <RoleBadge key={r} role={r} />
                    ))}
                  </div>
                </motion.button>
              ))}
        </div>

        {companies && companies.length === 0 ? (
          <div className="mt-6 rounded-xl border border-dashed border-border p-10 text-center">
            <ShieldCheck className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">
              Vi fant ingen selskaper der du har en rolle ennå.
            </p>
          </div>
        ) : null}

        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          {/* Alle styremøter */}
          <Accordion type="single" collapsible className="rounded-xl border border-border/60 bg-card shadow-sm">
            <AccordionItem value="meetings" className="border-b-0">
              <AccordionTrigger data-testid="dash-meetings-accordion" className="px-6 py-4 hover:no-underline">
                <span className="flex items-center gap-2">
                  <span className="font-headings text-lg font-bold tracking-tight">Alle styremøter</span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{dash ? dash.meetings_count : ""}</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="p-0">
                <div className="divide-y divide-border/60 border-t border-border/60">
                  {dash === null
                    ? Array.from({ length: 3 }).map((_, i) => <div key={i} className="p-4"><Skeleton className="h-12 rounded-lg" /></div>)
                    : [...(dash.upcoming_meetings || []), ...(dash.recent_meetings || [])].slice(0, 6).map((m) => (
                        <button key={m.id} data-testid={`dash-meeting-${m.id}`} onClick={() => navigate(`/selskap/${m.company_id}/moter/${m.id}`)} className="group flex w-full items-center gap-3 px-6 py-3.5 text-left transition-colors hover:bg-muted/50">
                          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><CalendarClock className="h-5 w-5" strokeWidth={1.7} /></div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium group-hover:text-primary">{m.title}</p>
                            <p className="truncate text-xs text-muted-foreground">{m.company_name} · {formatDate(m.date)} · {meetingTypeLabel(m.type)}</p>
                          </div>
                          <StatusBadge status={m.status} />
                        </button>
                      ))}
                  {dash && dash.meetings_count === 0 ? <p className="p-6 text-sm text-muted-foreground">Ingen møter ennå.</p> : null}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Alle oppgaver */}
          <Accordion type="single" collapsible className="rounded-xl border border-border/60 bg-card shadow-sm">
            <AccordionItem value="tasks" className="border-b-0">
              <AccordionTrigger data-testid="dash-tasks-accordion" className="px-6 py-4 hover:no-underline">
                <span className="flex items-center gap-2">
                  <span className="font-headings text-lg font-bold tracking-tight">Mine oppgaver</span>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{dash ? dash.my_open_tasks_count : ""} åpne</span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="p-0">
                <div className="divide-y divide-border/60 border-t border-border/60">
                  {dash === null
                    ? Array.from({ length: 3 }).map((_, i) => <div key={i} className="p-4"><Skeleton className="h-12 rounded-lg" /></div>)
                    : (dash.my_tasks || []).slice(0, 7).map((t) => (
                        <button key={t.id} data-testid={`dash-task-${t.id}`} onClick={() => navigate(`/selskap/${t.company_id}/oppgaver`)} className={`group flex w-full items-center gap-3 px-6 py-3.5 text-left transition-colors hover:bg-muted/50 ${t.status === "ferdig" ? "opacity-60" : ""}`}>
                          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><CheckSquare className="h-5 w-5" strokeWidth={1.7} /></div>
                          <div className="min-w-0 flex-1">
                            <p className={`truncate text-sm font-medium group-hover:text-primary ${t.status === "ferdig" ? "line-through" : ""}`}>{t.title}</p>
                            <p className="truncate text-xs text-muted-foreground">{t.company_name}{t.assignee_name ? ` · ${t.assignee_name}` : ""}{t.due_date ? ` · frist ${formatDate(t.due_date)}` : ""}</p>
                          </div>
                          <StatusBadge status={t.status} />
                        </button>
                      ))}
                  {dash && (dash.my_tasks || []).length === 0 ? <p className="p-6 text-sm text-muted-foreground">Ingen oppgaver er tildelt deg.</p> : null}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </main>
    </div>
  );
}
